<script setup>
import { computed, ref, onMounted, watch } from 'vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const props = defineProps({
  attachment: {
    type: Object,
    required: true,
  },
});

const showGallery = ref(false);
const { filteredCurrentChatAttachments, contentAttributes } = useMessageContext();
const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const isBlockedHistoricalMedia = computed(() => {
  return (
    !!contentAttributes.value?.blockedHistoricalMedia ||
    !!props.attachment?.blockedHistoricalMedia
  );
});

onMounted(() => {
  if (!isBlockedHistoricalMedia.value && props.attachment?.dataUrl) {
    loadWithRetry(props.attachment.dataUrl);
  }
});

watch(
  () => props.attachment?.dataUrl,
  newUrl => {
    if (!isBlockedHistoricalMedia.value && newUrl && !isLoaded.value) {
      hasError.value = false;
      loadWithRetry(newUrl);
    }
  }
);

const handleGalleryError = () => {
  hasError.value = true;
};

const openGallery = () => {
  if (!isBlockedHistoricalMedia.value) {
    showGallery.value = true;
  }
};
</script>

<template>
  <div
    class="size-[72px] overflow-hidden contain-content rounded-xl cursor-pointer"
    @click="openGallery"
  >
    <div
      v-if="isBlockedHistoricalMedia"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-2 text-n-slate-11"
    >
      <Icon icon="i-lucide-eye-off" class="text-n-slate-11" />
      Conteúdo bloqueado
    </div>
    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center gap-1 text-xs text-center rounded-lg size-full bg-n-alpha-1 text-n-slate-11"
    >
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      {{ $t('COMPONENTS.MEDIA.LOADING_FAILED') }}
    </div>
    <div
      v-else-if="!isLoaded"
      class="size-full bg-n-alpha-2 animate-pulse rounded-xl"
    />
    <img
      v-else
      class="object-cover w-full h-full skip-context-menu"
      :src="attachment.dataUrl"
    />
  </div>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleGalleryError"
    @close="() => (showGallery = false)"
  />
</template>
